import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
} from '@mui/material';
import LoadingButton from '../../utils/LoadingButton';
import logger from '../../../logger';
import { alertError, alertSuccess } from '../../../alertDispatcher';
import { getCurrentMission, getCurrentSession } from '../../../dataModelHelpers';
import { dispatchMissionCreatedDeleted } from '../../../missionEvents';
import { CustomCheckbox } from '../../utils/CustomCheckbox';
import { LocalAtRecord, Job, Mission } from '../../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { getLocalAtSearch } from '../../../db_ops/airtable_search_ops';

interface SkipsProcessingDialogProps {
  closeForm: () => void;
  open: boolean;
}

type SkipsHandling = 'Permanent Skip' | 'Temporary Skip' | 'None';
const DEFAULT_SKIPS_HANDLING: SkipsHandling = 'Temporary Skip';
const DEFAULT_DELETED_SAMPLES = false;

const SkipsProcessingDialog: React.FC<SkipsProcessingDialogProps> = ({ closeForm, open }) => {
  const [skipsHandlingOptions, setSkipsHandlingOptions] = useState<SkipsHandling[]>([
    'Permanent Skip',
    'Temporary Skip',
    'None',
  ]);
  const [deletedSamples, setDeletedSamples] = useState<boolean>(DEFAULT_DELETED_SAMPLES);
  const [skipsHandling, setSkipsHandling] = useState<SkipsHandling>(DEFAULT_SKIPS_HANDLING);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const getRelevantJobInfo = async () => {
      const mission = getCurrentMission();

      if (!mission) {
        return;
      }

      if (!mission.job_id) {
        return;
      }

      // If there are any skipped samples, remove the 'None' option
      const skippedSamplesPresent = mission.getSamples().some((sample) => sample.skipped_or_deleted);

      if (skippedSamplesPresent) {
        setSkipsHandlingOptions(['Permanent Skip', 'Temporary Skip']);
        setSkipsHandling(DEFAULT_SKIPS_HANDLING);
      }

      // Get original job fresh record to compare samples number
      const originalJobRecord = await Job.getJobRecordById(mission.job_id, true);

      if (!originalJobRecord) {
        return;
      }

      // If a user deleted samples, set deletedSamples to true
      const currentSamplesNumber = mission.getSamples().length;
      const originalSamplesNumber = originalJobRecord.get('# Samples');

      if (currentSamplesNumber !== originalSamplesNumber) {
        setDeletedSamples(true);
      }
    };

    getRelevantJobInfo();
  }, []);

  const handleClose = () => {
    // Reset state
    setDeletedSamples(DEFAULT_DELETED_SAMPLES);
    setSkipsHandling(DEFAULT_SKIPS_HANDLING);
    setErrorMessage(null);
    closeForm();
  };

  const updateExeMission = async (mission: Mission): Promise<boolean> => {
    const missionName = mission.getMissionName('EXE');

    try {
      let record = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', mission.job_id!);

      if (!record) {
        await getLocalAtSearch<JobsFieldSet>('Jobs', '', `{Rogo Job ID} = '${mission.job_id}'`, true);
        record = LocalAtRecord.getRecord('Jobs', mission.job_id!);
      }

      if (!record) {
        throw new Error('Airtable record not found');
      }

      // Generate KML
      const kml = mission.to_kml({ insert_boxes: true });

      if (!kml) {
        throw new Error('Failed to generate KML');
      }

      // Clear and upload Exe Mission
      await record.clear_attachments('Exe Mission');
      const attachment = await record.new_attachment('Exe Mission');
      const kmz = await kml.toKMZ(`${missionName}.kml`);
      await attachment.write(
        `${missionName}`,
        'kmz',
        new Blob([kmz as BlobPart], { type: 'application/vnd.google-earth.kmz' }),
      );

      // Set the fields
      await record.set('Deleted Sples in Exe? (Skip Mgmt)', deletedSamples ? 'Y' : 'N');

      // Set skips handling - null if None, otherwise the value
      await record.set('Skips in Exe? (Skip Mgmt)', skipsHandling);

      // Add job flag without removing existing flags
      const existingFlags = (record.get('Job Flags') as string[]) || [];
      const newFlags = [...new Set([...existingFlags, 'Skips - Run Code Trigger'])];
      await record.set('Job Flags', newFlags);

      // Sync the record
      await record.sync();

      await logger.log('SkipsProcessingDialog - Exe mission updated successfully', {
        jobId: mission.job_id,
        deletedSamples,
        skipsHandling,
      });

      return true;
    } catch (error) {
      console.error('SkipsProcessingDialog - updateExeMission: error', error);

      throw error;
    }
  };

  const handleUpdateExe = async () => {
    setErrorMessage(null);

    const mission = getCurrentMission();

    if (!mission) {
      alertError('No mission found, try to reload (in the sidebar).');
      setErrorMessage('No mission found');

      return;
    }

    const session = getCurrentSession();

    if (!session) {
      alertError('No session found, try to reload (in the sidebar).');
      setErrorMessage('No session found');

      return;
    }

    // Check that thare are no skipped samples with type "Delete"
    const samplesWithTypeDelete = mission.getSamples().some((sample) => sample.isSkippedWithTypeDelete());

    if (samplesWithTypeDelete) {
      setErrorMessage('Cannot process mission with samples skipped as "Delete".');

      return;
    }

    try {
      const result = await updateExeMission(mission);

      if (result) {
        // Clear mission from session
        session.Mission_id = undefined;
        dispatchMissionCreatedDeleted();

        // Close dialog
        handleClose();

        alertSuccess(`Successfully updated job ${mission.job_id || ''}`);
      }
    } catch (error) {
      console.error('SkipsProcessingDialog - handleUpdateExe: error', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      setErrorMessage(`Failed to update: ${errorMsg}`);
    }
  };

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth="sm"
      open={open || false}
      fullWidth
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
      }}
    >
      <DialogTitle>Skips Processing</DialogTitle>
      <DialogContent style={{ paddingTop: 20 }}>
        <Grid container spacing={2} size={12}>
          <Grid size={12}>
            <FormControlLabel
              control={
                <CustomCheckbox
                  checked={deletedSamples}
                  color="primary"
                  onChange={(e) => setDeletedSamples(e.target.checked)}
                />
              }
              label="Did you delete any samples?"
            />
          </Grid>

          <Grid size={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Skips handling</FormLabel>
              <RadioGroup value={skipsHandling} onChange={(e) => setSkipsHandling(e.target.value as SkipsHandling)}>
                {skipsHandlingOptions.map((option) => (
                  <FormControlLabel key={option} value={option} control={<Radio color="primary" />} label={option} />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>

          {errorMessage && (
            <Grid size={12}>
              <div style={{ color: 'red' }}>
                <b>{errorMessage}</b>
              </div>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="outlined" color="primary" onClick={handleUpdateExe}>
          Start Process
        </LoadingButton>

        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkipsProcessingDialog;
